// Copyright (c) Microsoft. All rights reserved.

import { FC, useEffect } from 'react';

import {
    Avatar,
    Persona,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    Tooltip,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import { IChatUser } from '../../../libs/models/ChatUser';
import { ScrollBarStyles } from '../../../styles';
import { KeyboardShiftUppercase20Filled, PersonDelete20Filled } from '@fluentui/react-icons';
import { useChat } from '../../../libs/hooks/useChat';
import { RootState } from '../../../redux/app/store';
import { useAppSelector } from '../../../redux/app/hooks';
import React from 'react';
import { useDispatch } from "react-redux";
import { setNewChatOwner, setUsersForConversation } from '../../../redux/features/conversations/conversationsSlice';

interface ShareBotMenuProps {
    participants: IChatUser[];
}

const useClasses = makeStyles({
    root: {
        display: 'flex',
        ...ScrollBarStyles,
        ...shorthands.padding(tokens.spacingVerticalNone, tokens.spacingVerticalXS),
        ...shorthands.border(tokens.spacingVerticalM),
        ...shorthands.borderRight(tokens.spacingHorizontalXS),
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        height: 'min-content',
        maxHeight: '220px',
        width: '200px',
        overflowWrap: 'anywhere',
        ...shorthands.gap(tokens.spacingVerticalS),
    },
    listItem: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
});

export const ParticipantsList: FC<ShareBotMenuProps> = ({participants}) => {
    const classes = useClasses();
    const {conversations, selectedId} = useAppSelector((state: RootState) => state.conversations);
    const {activeUserInfo} = useAppSelector((state: RootState) => state.app);
    const [refresh, setRefresh] = React.useState(false);
    const chat = useChat();
    const dispatch = useDispatch();

    const handlePromoteClick = (participantId: string) => {
        chat.transferOwnership(selectedId, participantId)
            .then((chatSession) => {
                if (chatSession != null) {
                    dispatch(setNewChatOwner({chatId: selectedId, newOwner: participantId}));
                }
                setRefresh(!refresh);
            })
            .catch((error) => {
                console.error(`Error promoting participant: ${participantId}`, error);
            });
    };

    const handleDeleteClick = (participantId: string) => {
        chat.deleteParticipant(selectedId, participantId)
            .then(() => {
                setRefresh(!refresh);
            })
            .catch((error) => {
                console.error(`Error deleting participant: ${participantId}`, error);
            });
    };

    useEffect(() => {
        chat.getAllChatParticipants(selectedId)
            .then((newParticipants) => {
                if (newParticipants) {
                    dispatch(setUsersForConversation({chatId: selectedId, users: newParticipants}));
                }
            })
            .catch((error) => {
                console.error(`Error getting participants for chat: ${selectedId}`, error);
            });
    }, [refresh]);

    return (
        <Popover onOpenChange={() => {
            setRefresh(!refresh)
        }} positioning={'below'} size="small">
            <PopoverTrigger>
                <Tooltip content="Chat participants" relationship="label">
                    <Avatar initials={`+${Math.min(participants.length - 1, 100)}`}/>
                </Tooltip>
            </PopoverTrigger>
            <PopoverSurface className={classes.root}>
                <div className={classes.list}>
                    {participants.map((participant) => (
                        <div className={classes.listItem} key={participant.id}>
                            <Persona
                                textAlignment="center"
                                secondaryText={participant.id == conversations[selectedId].chatOwner ? 'Chat Owner' : ''}
                                name={participant.fullName}
                                key={participant.id}
                                avatar={{color: 'colorful'}}
                            />
                            {
                                conversations[selectedId].chatOwner == activeUserInfo?.id && participant.id != conversations[selectedId].chatOwner && (
                                    <div style={{cursor: 'pointer'}}>
                                        <Tooltip content={'Promote to Chat Owner'} relationship="label">
                                            <KeyboardShiftUppercase20Filled onClick={() => {
                                                handlePromoteClick(participant.id)
                                            }}/>
                                        </Tooltip>
                                        <Tooltip content={'Remove participant from your chat'} relationship="label">
                                            <PersonDelete20Filled onClick={() => {
                                                handleDeleteClick(participant.id)
                                            }}/>
                                        </Tooltip>
                                    </div>
                                )
                            }
                        </div>
                    ))}
                </div>
            </PopoverSurface>
        </Popover>
    );
};
